import React, { CSSProperties } from 'react'

import { SPLIT_FRACTION_VAR, useSplitFraction } from '../../useSplitFraction'
import { Action, Actions, SplitButton } from './EntityCompareActions.styles'

interface EntityCompareActionsProps {
  /** Both versions have it at the same size, so it can be laid side by side. */
  canSplit: boolean
  isSplit: boolean
  /** Which side the canvas draws the compared version on, so the button agrees. */
  sourceOnRight: boolean
  onToggleSplit(): void
}

/**
 * The treatments a selected changed entity can be read with.
 *
 * Each treatment is asked for rather than applied by selecting: selecting is
 * how a reader looks at something, and split and swap both want that same
 * click. Picking one leaves the other reachable.
 *
 * Swap and overlay are here and disabled so the set is visible from the start;
 * they arrive with their own chunks.
 */
export function EntityCompareActions({
  canSplit,
  isSplit,
  sourceOnRight,
  onToggleSplit,
}: EntityCompareActionsProps) {
  // Only this button follows the line, so only this button renders as it moves
  const fraction = useSplitFraction()

  return (
    <Actions
      data-testid="compare-entity-actions"
      // The overlay these sit in opens its entity on double-click, and turning a
      // treatment on and straight off again would otherwise do it
      onDoubleClick={event => event.stopPropagation()}
    >
      <SplitButton
        type="button"
        $active={isSplit}
        $sourceOnRight={sourceOnRight}
        style={
          isSplit
            ? ({ [SPLIT_FRACTION_VAR]: `${fraction * 100}%` } as CSSProperties)
            : undefined
        }
        aria-pressed={isSplit}
        disabled={!canSplit}
        title={
          canSplit
            ? undefined
            : 'This changed size, so the two cannot be laid side by side'
        }
        onClick={onToggleSplit}
      >
        Split
      </SplitButton>

      <Action type="button" disabled title="Not available yet">
        Swap
      </Action>

      <Action type="button" disabled title="Not available yet">
        Overlay
      </Action>
    </Actions>
  )
}
